/**
 * Helpers for scoping notifications to the currently selected organization.
 *
 * Notifications carry an optional `organization` (added for org-scoping). The
 * backend stores it as an ObjectId (serialized to a string); guard against a
 * populated object or a missing value (legacy notifications) too.
 */

import {
    formatDateIST,
    todayISTDateKey,
    toISTDateKey,
    yesterdayISTDateKey,
} from "./formatDateIST";

type NotifOrg = string | { _id: string } | null | undefined;

/** Resolve the organization id a notification belongs to (or undefined). */
export const getNotificationOrgId = (organization?: NotifOrg): string | undefined => {
    if (!organization) return undefined;
    return typeof organization === "string" ? organization : organization._id;
};

/**
 * Predicate for the notification list / bell drawer (lenient scoping).
 *
 * A notification is shown when it belongs to the selected org OR when it has no
 * organization at all (legacy / global notifications) - so nothing important
 * ever disappears, while org-specific items are correctly filtered out of other
 * orgs. When no org is selected yet, everything is shown.
 */
export const isNotificationInOrgScope = (
    organization: NotifOrg,
    selectedOrgId: string | undefined
): boolean => {
    if (!selectedOrgId) return true;
    const orgId = getNotificationOrgId(organization);
    if (!orgId) return true;
    return orgId === selectedOrgId;
};

/**
 * Strict predicate for the sidebar badges: a notification only counts when it
 * explicitly belongs to the selected org. Untagged items are ignored so a
 * stale / cross-org item never lights the badge.
 */
export const isNotificationInOrgStrict = (
    organization: NotifOrg,
    selectedOrgId: string | undefined
): boolean => {
    return !!selectedOrgId && getNotificationOrgId(organization) === selectedOrgId;
};

/** Parse a notification timestamp from API / SSE payloads. */
export const parseNotificationDate = (
    date: string | Date | null | undefined
): Date | null => {
    if (date == null || date === "") return null;
    const parsed = date instanceof Date ? date : new Date(date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Stable IST calendar key (YYYY-MM-DD) for grouping. */
export const toNotificationLocalDateKey = (
    date: string | Date | null | undefined
): string | null => {
    return toISTDateKey(date);
};

/** Human-readable section header for a YYYY-MM-DD grouping key (IST). */
export const formatNotificationDateLabel = (key: string): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!match) return "Unknown date";

    const todayKey = todayISTDateKey();
    const yesterdayKey = yesterdayISTDateKey();
    if (key === todayKey) return "Today";
    if (key === yesterdayKey) return "Yesterday";

    const d = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
    );
    if (Number.isNaN(d.getTime())) return "Unknown date";

    return formatDateIST(d, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    });
};

/** Compact relative time for notification cards. */
export const formatNotificationRelativeTime = (
    date: string | Date | null | undefined
): string => {
    const parsed = parseNotificationDate(date);
    if (!parsed) return "";
    const diff = Date.now() - parsed.getTime();
    const sec = Math.round(diff / 1000);
    if (sec < 45) return "just now";
    if (sec < 90) return "1 min ago";
    const min = Math.round(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.round(hr / 24);
    if (day < 7) return `${day} d ago`;
    const wk = Math.round(day / 7);
    if (wk < 5) return `${wk} wk ago`;
    return formatDateIST(parsed);
};

/** Default time windows for notification fetching. */
export const NOTIFICATION_DRAWER_DAYS = 7;
export const NOTIFICATION_PAGE_INITIAL_DAYS = 30;
export const NOTIFICATION_PAGE_LOAD_MORE_DAYS = 15;

/** Subtract calendar days from a date. */
export const subtractDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
};

/** ISO timestamp for the start of a lookback window (now minus days). */
export const notificationWindowFrom = (days: number): string =>
    subtractDays(new Date(), days).toISOString();

/** Whether a notification falls within the last N calendar days. */
export const isWithinLastDays = (
    date: string | Date | null | undefined,
    days: number
): boolean => {
    const parsed = parseNotificationDate(date);
    if (!parsed) return false;
    return parsed >= subtractDays(new Date(), days);
};
